import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Grid,
  Box,
  Typography,
  CircularProgress,
  Button,
  Pagination,
  ToggleButton,
  ToggleButtonGroup,
  Fab,
  Skeleton,
  useMediaQuery,
  useTheme,
  alpha,
  Fade,
  Slide
} from '@mui/material';
import {
  GridView as GridViewIcon,
  ViewList as ViewListIcon,
  FilterList as FilterListIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon
} from '@mui/icons-material';
import { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';
import MobileOptimizedProductCard from './MobileOptimizedProductCard';
import { InventoryItem } from '../../utils/types';
import { getStoreProducts } from '../../services/storeService';

interface MobileOptimizedProductListProps {
  categoryId?: string;
  searchQuery?: string;
  itemsPerPage?: number;
}

const MobileOptimizedProductList: React.FC<MobileOptimizedProductListProps> = ({
  categoryId,
  searchQuery,
  itemsPerPage = 12
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  
  const [products, setProducts] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastVisible, setLastVisible] = useState<QueryDocumentSnapshot<DocumentData> | undefined>(undefined);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showScrollTop, setShowScrollTop] = useState(false);

  // تحديد عدد الأعمدة حسب حجم الشاشة ونوع العرض
  const getGridColumns = useMemo(() => {
    if (viewMode === 'list') return 1;
    if (isMobile) return 2;
    if (isTablet) return 3;
    return 4;
  }, [viewMode, isMobile, isTablet]);

  const loadProducts = useCallback(async (newCategory: boolean = false) => {
    try {
      setLoading(true);
      setError(null);

      if (newCategory) {
        setProducts([]);
        setLastVisible(undefined);
        setPage(1);
      }

      const result = await getStoreProducts(
        categoryId,
        newCategory ? undefined : lastVisible,
        itemsPerPage
      );

      if (!result || !result.products) {
        setError('حدث خطأ أثناء تحميل المنتجات. يرجى المحاولة مرة أخرى.');
        setLoading(false);
        return;
      }

      if (newCategory) {
        setProducts(result.products);
      } else {
        setProducts(prev => [...prev, ...result.products]);
      }

      setLastVisible(result.lastVisible || undefined);
      setHasMore(result.products.length === itemsPerPage);
    } catch (err) {
      console.error('Error loading products:', err);
      setError('حدث خطأ أثناء تحميل المنتجات. يرجى المحاولة مرة أخرى.');
    } finally {
      setLoading(false);
    }
  }, [categoryId, lastVisible, itemsPerPage]);

  useEffect(() => {
    loadProducts(true);
  }, [categoryId]);

  // مراقبة التمرير لإظهار زر العودة للأعلى
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // تصفية المنتجات حسب البحث
  const filteredProducts = useMemo(() => {
    if (!searchQuery) return products;
    return products.filter(product =>
      product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (product.description && product.description.toLowerCase().includes(searchQuery.toLowerCase()))
    );
  }, [products, searchQuery]);

  const handleViewModeChange = (_event: React.MouseEvent<HTMLElement>, newViewMode: 'grid' | 'list') => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
    }
  };

  const handleLoadMore = () => {
    if (hasMore && !loading) {
      loadProducts();
    }
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // حساب المنتجات المعروضة في الصفحة الحالية
  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPageProducts = filteredProducts.slice(startIndex, endIndex);
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);

  if (loading && products.length === 0) {
    return (
      <Box sx={{ py: 2 }}>
        {/* Skeleton للتحكم في العرض */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Skeleton variant="text" width={150} height={40} />
          <Skeleton variant="rectangular" width={120} height={40} sx={{ borderRadius: 2 }} />
        </Box>
        
        {/* Skeleton للمنتجات */}
        <Grid container spacing={2}>
          {Array.from({ length: getGridColumns * 3 }).map((_, index) => (
            <Grid item xs={12 / getGridColumns} key={index}>
              <Skeleton
                variant="rectangular"
                height={viewMode === 'list' ? 120 : 300}
                sx={{ borderRadius: 3 }}
              />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="error" variant="h6" gutterBottom>
          {error}
        </Typography>
        <Button
          variant="contained"
          onClick={() => loadProducts(true)}
          sx={{ mt: 2, borderRadius: 2 }}
        >
          إعادة المحاولة
        </Button>
      </Box>
    );
  }

  if (filteredProducts.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" gutterBottom>
          لا توجد منتجات متاحة{searchQuery ? ' تطابق بحثك' : ' في هذه الفئة'}
        </Typography>
        <Button
          variant="contained"
          onClick={() => loadProducts(true)}
          sx={{ mt: 2, borderRadius: 2 }}
        >
          إعادة المحاولة
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ py: 2 }}>
      {/* شريط التحكم */}
      <Fade in timeout={500}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
            p: 2,
            bgcolor: alpha(theme.palette.background.paper, 0.8),
            borderRadius: 3,
            backdropFilter: 'blur(10px)',
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            {filteredProducts.length} منتج
          </Typography>
          
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                borderRadius: 2,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                '&.Mui-selected': {
                  bgcolor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                  '&:hover': {
                    bgcolor: theme.palette.primary.dark
                  }
                }
              }
            }}
          >
            <ToggleButton value="grid" aria-label="عرض شبكي">
              <GridViewIcon />
            </ToggleButton>
            <ToggleButton value="list" aria-label="عرض قائمة">
              <ViewListIcon />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Fade>

      {/* قائمة المنتجات */}
      <Grid container spacing={viewMode === 'list' ? 1 : 2}>
        {currentPageProducts.map((product, index) => (
          <Grid
            item
            xs={viewMode === 'list' ? 12 : 12 / getGridColumns}
            key={product.id}
          >
            <Slide
              in
              direction="up"
              timeout={300 + index * 50}
              style={{ transitionDelay: `${index * 50}ms` }}
            >
              <div>
                <MobileOptimizedProductCard
                  product={product}
                  layout={viewMode}
                  showQuickActions={!isMobile}
                  priority={index < 4}
                />
              </div>
            </Slide>
          </Grid>
        ))}
      </Grid>

      {/* التنقل بين الصفحات */}
      {filteredProducts.length > itemsPerPage && (
        <Fade in timeout={500}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              size={isMobile ? 'medium' : 'large'}
              showFirstButton
              showLastButton
              sx={{
                '& .MuiPaginationItem-root': {
                  borderRadius: 2,
                  fontWeight: 'bold'
                }
              }}
            />
          </Box>
        </Fade>
      )}

      {/* زر تحميل المزيد */}
      {!searchQuery && hasMore && (
        <Fade in timeout={500}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Button
              variant="outlined"
              onClick={handleLoadMore}
              disabled={loading}
              size="large"
              sx={{
                borderRadius: 3,
                px: 4,
                py: 1.5,
                fontWeight: 'bold',
                border: `2px solid ${theme.palette.primary.main}`,
                '&:hover': {
                  border: `2px solid ${theme.palette.primary.dark}`,
                  bgcolor: alpha(theme.palette.primary.main, 0.1)
                }
              }}
            >
              {loading ? <CircularProgress size={24} /> : 'تحميل المزيد'}
            </Button>
          </Box>
        </Fade>
      )}

      {/* زر العودة للأعلى */}
      <Slide in={showScrollTop} direction="up" timeout={300}>
        <Fab
          color="primary"
          size="medium"
          onClick={scrollToTop}
          sx={{
            position: 'fixed',
            bottom: 20,
            left: 20,
            zIndex: 1000,
            boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.3)}`
          }}
        >
          <KeyboardArrowUpIcon />
        </Fab>
      </Slide>
    </Box>
  );
};

export default MobileOptimizedProductList;
